/* ============================================================
 * Idées reçues — ouverture des cartes
 * ------------------------------------------------------------
 * 1. Verrou : la date d'ouverture est re-calculée ici, côté
 *    client. Le rendu Liquid passe par le cache CDN de Shopify
 *    et peut dater de la veille — lui seul ne suffit pas.
 * 2. Retournement : une classe sur la carte, le reste est en CSS.
 *    Le recto est désactivé quand la carte est ouverte, le verso
 *    est neutralisé quand elle est fermée : sans ça, la tabulation
 *    traverse des faces invisibles.
 * 3. Mémoire : les cartes déjà ouvertes sont gardées en
 *    localStorage pour la pastille et le compteur. Tout est dans
 *    des try/catch — navigation privée, stockage bloqué, iframe
 *    de l'éditeur de thème.
 * ============================================================ */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'bnz-ir:';

  /* ----------------------------------------------------------
   * Utilitaires
   * -------------------------------------------------------- */

  // 'AAAA-MM-JJ' -> Date au minuit local. new Date('2026-12-01')
  // parserait en UTC : à Paris la carte s'ouvrirait le 30/11 à 01h.
  function parseUnlockDate(value) {
    if (!value) return null;
    var parts = String(value).trim().split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    var day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  function previewUnlocked() {
    try {
      return new URLSearchParams(window.location.search).get('ir_unlock') === '1';
    } catch (e) {
      return false;
    }
  }

  function readSeen(key) {
    try {
      var raw = window.localStorage.getItem(STORAGE_PREFIX + key);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeSeen(key, ids) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(ids));
    } catch (e) {
      /* stockage indisponible : la page reste utilisable sans mémoire */
    }
  }

  // Retire ou rend le contenu d'une face au clavier. `inert` fait le
  // travail quand il existe, sinon on range les tabindex à la main.
  function setFocusable(container, enabled) {
    if (!container) return;

    if ('inert' in HTMLElement.prototype) {
      container.inert = !enabled;
      return;
    }

    container.querySelectorAll('a[href], button, [tabindex]').forEach(function (el) {
      if (enabled) {
        if (el.dataset.irTabindex === '') {
          el.removeAttribute('tabindex');
        } else if (el.dataset.irTabindex) {
          el.setAttribute('tabindex', el.dataset.irTabindex);
        }
        delete el.dataset.irTabindex;
      } else {
        el.dataset.irTabindex = el.getAttribute('tabindex') || '';
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  /* ----------------------------------------------------------
   * Section
   * -------------------------------------------------------- */
  function initSection(section) {
    if (!section || section.dataset.irBound === '1') return;
    section.dataset.irBound = '1';

    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-ir-card]'));
    if (!cards.length) return;

    var storageKey = section.dataset.storageKey || 'default';
    var singleOpen = section.dataset.singleOpen !== 'false';
    var unlockAll = section.dataset.unlockAll === 'true' || previewUnlocked();

    var progress = section.querySelector('[data-ir-progress]');
    var progressCount = section.querySelector('[data-ir-count]');

    var seen = readSeen(storageKey);

    function isSeen(id) {
      return seen.indexOf(id) !== -1;
    }

    function markSeen(card) {
      var id = card.dataset.cardId;
      if (!id || isSeen(id)) return;
      seen.push(id);
      writeSeen(storageKey, seen);
      card.classList.add('is-seen');
      refreshProgress();
    }

    function refreshProgress() {
      if (!progress || !progressCount) return;
      var count = cards.filter(function (card) {
        return isSeen(card.dataset.cardId);
      }).length;
      progressCount.textContent = count;
      progress.hidden = count === 0;
    }

    function close(card, returnFocus) {
      if (!card.classList.contains('is-open')) return;
      var trigger = card.querySelector('[data-ir-open]');
      card.classList.remove('is-open');
      setFocusable(card.querySelector('[data-ir-back]'), false);
      if (trigger) {
        trigger.disabled = false;
        trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) trigger.focus();
      }
    }

    function open(card) {
      if (card.classList.contains('is-locked') || card.classList.contains('is-open')) return;

      if (singleOpen) {
        cards.forEach(function (other) {
          if (other !== card) close(other, false);
        });
      }

      var trigger = card.querySelector('[data-ir-open]');
      var back = card.querySelector('[data-ir-back]');

      card.classList.add('is-open');
      setFocusable(back, true);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
        // Le recto est retourné, donc invisible : il ne doit plus
        // capter la tabulation.
        trigger.disabled = true;
      }

      markSeen(card);

      var closeBtn = card.querySelector('[data-ir-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    function refuse(card) {
      card.classList.remove('is-refused');
      // Reflow : sans ça, deux clics d'affilée ne rejouent pas l'animation.
      void card.offsetWidth;
      card.classList.add('is-refused');
      window.setTimeout(function () {
        card.classList.remove('is-refused');
      }, 450);
    }

    cards.forEach(function (card) {
      var trigger = card.querySelector('[data-ir-open]');
      var back = card.querySelector('[data-ir-back]');
      var unlockDate = parseUnlockDate(card.dataset.unlock);
      var locked = !unlockAll && unlockDate !== null && unlockDate.getTime() > Date.now();

      card.classList.toggle('is-locked', locked);
      if (isSeen(card.dataset.cardId)) card.classList.add('is-seen');

      setFocusable(back, false);

      if (trigger) {
        if (locked) {
          trigger.setAttribute('aria-disabled', 'true');
        } else {
          trigger.removeAttribute('aria-disabled');
        }

        trigger.addEventListener('click', function () {
          if (card.classList.contains('is-locked')) {
            refuse(card);
            return;
          }
          open(card);
        });
      }

      var closeBtn = card.querySelector('[data-ir-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          close(card, true);
        });
      }
    });

    section.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openCard = section.querySelector('.bnz-ir__card.is-open');
      if (!openCard) return;
      event.stopPropagation();
      close(openCard, true);
    });

    refreshProgress();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-bnz-ir]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* ----------------------------------------------------------
   * Éditeur de thème
   * -------------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Sélectionner un bloc dans l'éditeur ouvre la carte correspondante,
  // sinon le marchand édite un verso qu'il ne voit pas.
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest ? event.target.closest('[data-ir-card]') : null;
    if (!card) return;
    var trigger = card.querySelector('[data-ir-open]');
    if (trigger) trigger.click();
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var card = event.target.closest ? event.target.closest('[data-ir-card]') : null;
    if (!card) return;
    var closeBtn = card.querySelector('[data-ir-close]');
    if (closeBtn) closeBtn.click();
  });
})();
